import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Search, Plus, Eye, RefreshCw, Calendar, RotateCcw, Package,
  FileText, IndianRupee, Barcode, Trash2, Gift
} from 'lucide-react';

const SalesReturn = () => {
  const { user } = useAuth();
  const [returns, setReturns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  // Bill selection for return
  const [billSearch, setBillSearch] = useState('');
  const [pendingBills, setPendingBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [returnableItems, setReturnableItems] = useState([]);
  const [billCustomer, setBillCustomer] = useState(null);

  // Return form
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [returnItems, setReturnItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  const fetchReturns = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      const response = await api.get('/sales-returns', { params });
      setReturns(response.data);
    } catch (error) {
      console.error('Error fetching returns:', error);
    }
  }, [fromDate, toDate, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get('/sales-returns/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (canAccess) {
      fetchReturns();
      fetchStats();
    }
  }, [canAccess, fetchReturns, fetchStats]);

  // Search bills by bill number
  const searchBills = async () => {
    if (!billSearch.trim()) return;
    try {
      // Search across all bills (both pending and paid)
      const response = await api.get('/sales-billing/completed-payments', { params: { search: billSearch } });
      const pendingRes = await api.get('/sales-billing/pending-payments', { params: { search: billSearch } });
      const allBills = [...(response.data || []), ...(pendingRes.data || [])];
      // Deduplicate by id
      const unique = [];
      const seen = new Set();
      for (const b of allBills) {
        if (!seen.has(b.id)) {
          seen.add(b.id);
          unique.push(b);
        }
      }
      setPendingBills(unique);
    } catch (error) {
      console.error('Error searching bills:', error);
    }
  };

  // Load returnable items for selected bill
  const loadReturnableItems = async (bill) => {
    try {
      const response = await api.get(`/sales-returns/bill/${bill.id}/returnable`);
      setSelectedBill(response.data.bill);
      setBillCustomer(response.data.customer);
      setReturnableItems(response.data.returnable_items);
      setReturnItems([]);
    } catch (error) {
      toast.error('Failed to load returnable items');
    }
  };

  // Toggle item for return
  const toggleReturnItem = (item) => {
    setReturnItems(prev => {
      const exists = prev.find(ri => ri.bill_item_index === item.bill_item_index);
      if (exists) {
        return prev.filter(ri => ri.bill_item_index !== item.bill_item_index);
      }
      return [...prev, {
        bill_item_index: item.bill_item_index,
        description: item.description,
        quantity: item.returnable_qty,
        unit_price: item.unit_price,
        return_serials: [],
        is_foc: item.is_foc || false,
        available_serials: item.available_serials || [],
        has_serials: (item.available_serials || []).length > 0
      }];
    });
  };

  // Update return item quantity
  const updateReturnItemQty = (index, qty) => {
    setReturnItems(prev => prev.map((ri, i) =>
      i === index ? { ...ri, quantity: Math.min(Math.max(1, qty), returnableItems.find(x => x.bill_item_index === ri.bill_item_index)?.returnable_qty || qty) } : ri
    ));
  };

  // Toggle serial selection for return
  const toggleReturnSerial = (itemIndex, serial) => {
    setReturnItems(prev => prev.map((ri, i) => {
      if (i !== itemIndex) return ri;
      const existing = ri.return_serials.includes(serial);
      const newSerials = existing
        ? ri.return_serials.filter(s => s !== serial)
        : [...ri.return_serials, serial];
      return { ...ri, return_serials: newSerials, quantity: newSerials.length || ri.quantity };
    }));
  };

  // Submit return
  const handleSubmit = async () => {
    if (!selectedBill) { toast.error('Please select a bill'); return; }
    if (returnItems.length === 0) { toast.error('Please select items to return'); return; }
    if (!reason.trim()) { toast.error('Please enter a reason for return'); return; }

    // Validate serial selections
    for (const ri of returnItems) {
      if (ri.has_serials && ri.return_serials.length === 0) {
        toast.error(`Please select serial numbers for "${ri.description}"`);
        return;
      }
      if (ri.has_serials && ri.return_serials.length !== ri.quantity) {
        toast.error(`Serial count must match quantity for "${ri.description}"`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        bill_id: selectedBill.id,
        return_date: returnDate,
        reason,
        notes: notes || '',
        items: returnItems.map(ri => ({
          bill_item_index: ri.bill_item_index,
          description: ri.description,
          quantity: ri.quantity,
          unit_price: ri.unit_price,
          return_serials: ri.return_serials,
          is_foc: ri.is_foc
        }))
      };

      const response = await api.post('/sales-returns', payload);
      toast.success(`Sales return created - ${response.data.sales_return?.return_no}`);
      setCreateDialogOpen(false);
      resetForm();
      fetchReturns();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create return');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setBillSearch('');
    setPendingBills([]);
    setSelectedBill(null);
    setReturnableItems([]);
    setBillCustomer(null);
    setReturnItems([]);
    setReturnDate(new Date().toISOString().split('T')[0]);
    setReason('');
    setNotes('');
  };

  const handleViewReturn = async (ret) => {
    try {
      const response = await api.get(`/sales-returns/${ret.id}`);
      setSelectedReturn(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load return details');
    }
  };

  const handleDelete = async (ret) => {
    if (!isAdmin) { toast.error('Only Admin can delete returns'); return; }
    if (!window.confirm(`Delete return ${ret.return_no}? This will reverse inventory and ledger changes.`)) return;
    try {
      await api.delete(`/sales-returns/${ret.id}`);
      toast.success('Return deleted and changes reversed');
      fetchReturns();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete return');
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchReturns(), fetchStats()]).finally(() => setLoading(false));
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>Only Admin and Backoffice Accounts Executive can access Sales Returns.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-returns">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Returns</h1>
          <p className="text-gray-600 mt-1">Manage product returns and inventory restoration</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} disabled={loading} variant="outline">
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="bg-orange-600 hover:bg-orange-700" data-testid="create-return-btn">
            <Plus size={18} className="mr-2" />
            New Return
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Total Returns</p>
                <p className="text-3xl font-bold text-orange-800">{stats.total_returns || 0}</p>
              </div>
              <RotateCcw className="h-10 w-10 text-orange-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Return Amount</p>
                <p className="text-3xl font-bold text-red-800">₹{(stats.total_return_amount || 0).toLocaleString()}</p>
              </div>
              <IndianRupee className="h-10 w-10 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Items Returned</p>
                <p className="text-3xl font-bold text-blue-800">{stats.total_items_returned || 0}</p>
              </div>
              <Package className="h-10 w-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input placeholder="Search by return #, bill #, customer..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="return-search" />
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Calendar size={18} className="text-gray-400" />
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
          <span className="text-gray-400">to</span>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
        </div>
      </div>

      {/* Returns Table */}
      <Card>
        <CardHeader>
          <CardTitle>Return Records</CardTitle>
          <CardDescription>All sales returns (latest first)</CardDescription>
        </CardHeader>
        <CardContent>
          {returns.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <RotateCcw className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No return records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Return #</TableHead>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Return Amount</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {returns.map((ret) => (
                    <TableRow key={ret.id} data-testid={`return-${ret.id}`}>
                      <TableCell className="whitespace-nowrap">{ret.return_date}</TableCell>
                      <TableCell className="font-medium text-orange-700">{ret.return_no}</TableCell>
                      <TableCell className="text-blue-600">{ret.bill_no}</TableCell>
                      <TableCell>{ret.customer_name}</TableCell>
                      <TableCell>{(ret.items || []).length} item(s)</TableCell>
                      <TableCell className="font-semibold text-red-700">₹{(ret.return_amount || 0).toLocaleString()}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{ret.reason}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleViewReturn(ret)} data-testid={`view-return-${ret.id}`}>
                            <Eye size={14} />
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(ret)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Return Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw size={20} />
              Create Sales Return
            </DialogTitle>
            <DialogDescription>Return products against an existing bill</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Bill Search */}
            {!selectedBill && (
              <div className="space-y-2">
                <Label>Search Bill *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      placeholder="Search by bill number or customer name..."
                      value={billSearch}
                      onChange={(e) => setBillSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchBills()}
                      className="pl-9"
                      data-testid="bill-search-input"
                    />
                  </div>
                  <Button onClick={searchBills} variant="outline">Search</Button>
                </div>

                {pendingBills.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {pendingBills.map(bill => (
                      <div
                        key={bill.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => loadReturnableItems(bill)}
                        data-testid={`bill-option-${bill.id}`}
                      >
                        <div className="flex justify-between">
                          <div>
                            <p className="font-medium">{bill.bill_no}</p>
                            <p className="text-sm text-gray-500">{bill.customer_name} - {bill.bill_date}</p>
                          </div>
                          <p className="font-semibold">₹{(bill.net_amount || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected Bill Info */}
            {selectedBill && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-blue-800">{selectedBill.bill_no}</p>
                    <p className="text-sm text-blue-600">{billCustomer?.company_name} - {selectedBill.bill_date}</p>
                    <p className="text-sm text-blue-600">Bill Amount: ₹{(selectedBill.net_amount || 0).toLocaleString()}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedBill(null); setReturnableItems([]); setReturnItems([]); setBillCustomer(null); }}>
                    Change Bill
                  </Button>
                </div>
              </div>
            )}

            {/* Returnable Items */}
            {selectedBill && returnableItems.length > 0 && (
              <div className="space-y-2">
                <Label>Select Items to Return *</Label>
                <div className="border rounded-lg overflow-auto max-h-[250px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Select</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Returnable Qty</TableHead>
                        <TableHead>Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnableItems.map((item, idx) => {
                        const isSelected = returnItems.some(ri => ri.bill_item_index === item.bill_item_index);
                        return (
                          <TableRow key={idx} className={isSelected ? 'bg-orange-50' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleReturnItem(item)}
                                data-testid={`return-item-check-${idx}`}
                              />
                            </TableCell>
                            <TableCell>
                              <span>{item.description}</span>
                              {item.is_foc && <Badge className="ml-2 bg-amber-100 text-amber-800 text-xs"><Gift className="h-3 w-3 mr-1" />FOC</Badge>}
                            </TableCell>
                            <TableCell>{item.returnable_qty} (of {item.quantity})</TableCell>
                            <TableCell>{item.is_foc ? 'FOC' : `₹${(item.unit_price || 0).toLocaleString()}`}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {selectedBill && returnableItems.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>No returnable items left for this bill</p>
              </div>
            )}

            {/* Return Item Details (quantity & serial selection) */}
            {returnItems.length > 0 && (
              <div className="space-y-3">
                <Label>Return Details</Label>
                {returnItems.map((ri, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-sm">{ri.description} {ri.is_foc && <Badge className="bg-amber-100 text-amber-800 text-xs">FOC</Badge>}</p>
                      <span className="text-sm text-gray-500">{ri.is_foc ? 'FOC' : `₹${ri.unit_price} x ${ri.quantity}`}</span>
                    </div>

                    {!ri.has_serials && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Return Qty:</Label>
                        <Input
                          type="number"
                          min="1"
                          max={returnableItems.find(x => x.bill_item_index === ri.bill_item_index)?.returnable_qty || 1}
                          value={ri.quantity}
                          onChange={(e) => updateReturnItemQty(idx, parseInt(e.target.value) || 1)}
                          className="w-20 h-8"
                          data-testid={`return-qty-${idx}`}
                        />
                      </div>
                    )}

                    {ri.has_serials && ri.available_serials.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Select Serial Numbers to Return:</Label>
                        <div className="flex flex-wrap gap-2">
                          {ri.available_serials.map(sn => (
                            <div
                              key={sn}
                              onClick={() => toggleReturnSerial(idx, sn)}
                              className={`px-2 py-1 rounded text-xs font-mono cursor-pointer border transition-colors ${
                                ri.return_serials.includes(sn)
                                  ? 'bg-orange-100 border-orange-400 text-orange-800'
                                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                              data-testid={`return-serial-${sn}`}
                            >
                              <Barcode className="h-3 w-3 inline mr-1" />{sn}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">{ri.return_serials.length} selected</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Return Date */}
            <div className="space-y-2">
              <Label>Return Date *</Label>
              <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} data-testid="return-date" />
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason for Return *</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Defective product, Wrong item shipped..." rows={2} data-testid="return-reason" />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." rows={2} />
            </div>

            {/* Return Amount Summary */}
            {returnItems.length > 0 && (
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-orange-800">
                  Return Amount: ₹{returnItems.reduce((sum, ri) => sum + (ri.is_foc ? 0 : ri.quantity * ri.unit_price), 0).toLocaleString()}
                </p>
                <p className="text-xs text-orange-600 mt-1">This amount will be credited to the customer ledger</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedBill || returnItems.length === 0 || !reason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-return-btn"
            >
              {submitting ? 'Processing...' : 'Create Return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Return Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Return Details - {selectedReturn?.return_no}</DialogTitle>
          </DialogHeader>
          {selectedReturn && (
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg text-center">
                <p className="text-sm text-orange-600">Return Amount</p>
                <p className="text-3xl font-bold text-orange-800">₹{(selectedReturn.return_amount || 0).toLocaleString()}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Customer:</span>
                  <p className="font-medium">{selectedReturn.customer?.company_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Bill #:</span>
                  <p className="text-blue-600">{selectedReturn.bill?.bill_no || selectedReturn.bill_no}</p>
                </div>
                <div>
                  <span className="text-gray-500">Return Date:</span>
                  <p>{selectedReturn.return_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">Reason:</span>
                  <p>{selectedReturn.reason}</p>
                </div>
                {selectedReturn.notes && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Notes:</span>
                    <p>{selectedReturn.notes}</p>
                  </div>
                )}
              </div>

              {/* Returned Items */}
              <div>
                <Label className="text-sm font-semibold">Returned Items</Label>
                <div className="border rounded-lg mt-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedReturn.items || []).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium text-sm">{item.description}</p>
                            {item.is_foc && <Badge className="bg-amber-100 text-amber-800 text-xs mt-1"><Gift className="h-3 w-3 mr-1" />FOC</Badge>}
                            {item.return_serials && item.return_serials.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.return_serials.map(sn => (
                                  <Badge key={sn} variant="secondary" className="text-xs font-mono">
                                    <Barcode className="h-3 w-3 mr-1" />{sn}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.is_foc ? 'FOC' : `₹${(item.return_value || 0).toLocaleString()}`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="text-xs text-gray-400 border-t pt-2">
                Created by {selectedReturn.created_by_name} on {selectedReturn.created_at?.split('T')[0]}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesReturn;
